import { ChangeDetectionStrategy, Component, effect, inject, input, model, untracked } from '@angular/core';
import { signalState, patchState } from '@ngrx/signals';
import dayjs from 'dayjs';
import { Layout, Data, Config } from 'plotly.js-basic-dist-min';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { matchTolOption, defaultTolBez } from '../../_overlays/zaehlerverwaltung/zaehler.enums';
import {
  MessungÜbersicht,
  ToleranzenBezeichnungen,
} from '../../_overlays/zaehlerverwaltung/zaehler.types';

import { ClarityModule } from '@clr/angular';
import { PlotlyPlotModalComponent } from './plotly-plot-modal';

@Component({
  selector: 'app-messung-vergleich',
  templateUrl: './messung-vergleich.component.html',
  styleUrl: './messung-vergleich.component.scss',
  imports: [ClarityModule, PlotlyPlotModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessungVergleichComponent {
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly localeDate = inject(BackendLocaleDatePipe);

  readonly tol = input.required<ToleranzenBezeichnungen>();
  readonly row = model.required<MessungÜbersicht>();

  protected messungVglModal = signalState<{
    open: boolean;
    title: string;
    graph: {
      layout: Partial<Layout>;
      config: Partial<Config>;
      data: Data[];
    };
  }>({
    open: false,
    title: '',
    graph: {
      layout: {
        autosize: true,

        showlegend: true,
        legend: { orientation: 'h' },
        //width: 1000,
        // height: 630,
        margin: {
          l: 35,
          r: 20,
          b: 25,
          t: 20,
          pad: 0,
        },
        plot_bgcolor: 'transparent',
        paper_bgcolor: 'transparent',
        yaxis: {
          autorange: true,
          // range: [-10, 110],
          type: 'linear',
        },
      },
      config: {
        responsive: true,
        displaylogo: false,
        locale: 'de',
        modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
      },
      data: [],
    },
  });

  constructor() {
    effect(() => {
      const row = this.row();
      if (row) untracked(() => this.openMessungVgl(row));
    });
  }

  protected async openMessungVgl(row: MessungÜbersicht) {
    const Code = row.Bezeichnung ?? '---';

    let dates = Object.keys(row)
      .filter(key => dayjs(key, 'DD.MM.YYYY').isValid() && !key.startsWith('Tol'))
      .sort((a, b) => dayjs(b, 'DD.MM.YYYY').diff(dayjs(a, 'DD.MM.YYYY'), 'days') * -1);

    const measurements = dates.map(date => +row[date]?.toString().replace(',', '.'));

    const linies = ['Ist', 'Soll'];
    if (row.SR100plus) linies.push('SR100plus');
    if (row.SR100minus) linies.push('SR100minus');
    if (row.SRlimplus) linies.push('SRlimplus');
    if (row.SRlimminus) linies.push('SRlimminus');
    if (row.SRGplus) linies.push('SRGplus');
    if (row.SRGminus) linies.push('SRGminus');

    const names = linies.map(linie => {
      switch (linie) {
        case 'Ist':
          return Code;
        case 'Soll':
          return this.mrTranslate.transform('Soll-Maß');
        default:
          return this.tol[matchTolOption[linie]] ?? defaultTolBez[matchTolOption[linie]];
      }
    });

    const values = linies.map(linie => {
      if (linie == 'Ist') return measurements;
      if (linie == 'Soll') return Array(measurements.length).fill(+row.Soll);
      if (linie.endsWith('minus')) return Array(measurements.length).fill(+row.Soll - +row[linie]);
      if (linie.endsWith('plus')) return Array(measurements.length).fill(+row.Soll + +row[linie]);
    });

    const graph = { ...this.messungVglModal.graph() };

    const validMeasurements = [
      ...measurements,
      ...linies.map(linie => {
        if (linie == 'Soll') return +row.Soll;
        if (linie.endsWith('minus')) return +row.Soll - +row[linie];
        if (linie.endsWith('plus')) return +row.Soll + +row[linie];

        return NaN;
      }),
    ].filter(Number.isFinite);

    const min = Math.min(...validMeasurements);
    const max = Math.max(...validMeasurements);
    graph.layout.yaxis.range = [min - Math.abs(min) / 100, max + Math.abs(max) / 100];

    graph.data = linies.map((linie, i) => ({
      type: 'scatter',
      mode: linie == 'Ist' ? 'lines+markers' : 'lines',
      x: dates,
      y: values[i],
      name: names[i],
      line: this.getColor(names[i]),
    }));

    patchState(this.messungVglModal, {
      title: `${this.mrTranslate.transform('Messung')}: ${Code}`,
      open: true,
      graph,
    });
  }

  getColor(name) {
    if (name == 'Soll-Maß') {
      return { color: 'rgb(0, 0, 0)' };
    }
    if (name.includes('SR100')) {
      return { color: this.tol()['COLOR_100'] };
    }

    if (name.includes('SRLim')) {
      return { color: this.tol()['COLOR_L'] };
    }

    if (name.includes('SRG')) {
      return { color: this.tol()['COLOR_G'] };
    }
    return { color: 'rgb(53, 132, 187)' };
  }

  protected closeModal() {
    patchState(this.messungVglModal, { open: false });
    this.row.set(undefined);
  }
}
